import { useEffect, useRef, useState } from 'react';

/** Exponential smoothing for live telemetry readouts during replay. */
export function useSmoothedNumber(target: number | null, responseMs: number, enabled = true) {
  const [value, setValue] = useState<number | null>(target);
  const valueRef = useRef<number | null>(target);
  const targetRef = useRef<number | null>(target);

  useEffect(() => {
    targetRef.current = target;
    if (target === null) {
      if (!enabled) {
        valueRef.current = null;
        setValue(null);
      }
      return;
    }

    if (valueRef.current === null || !enabled) {
      valueRef.current = target;
      setValue(target);
    }
  }, [enabled, target]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    let frame = 0;
    let last = performance.now();

    const tick = (now: number) => {
      const nextTarget = targetRef.current;
      const current = valueRef.current;

      if (nextTarget !== null && current !== null) {
        const elapsedMs = now - last;
        const alpha = 1 - Math.exp(-elapsedMs / responseMs);
        const nextValue = current + (nextTarget - current) * alpha;
        valueRef.current = Math.abs(nextValue - nextTarget) < 0.001 ? nextTarget : nextValue;
        setValue(valueRef.current);
      }

      last = now;
      frame = window.requestAnimationFrame(tick);
    };

    frame = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(frame);
  }, [enabled, responseMs]);

  return value;
}
